/**
 * Internal sales CRM (super-admin, allowlisted). Ported from the standalone
 * ardealy-crm app: records stay plain strings, so values like "2000 USD", dates as
 * YYYY-MM-DD and comma-separated tags behave exactly as they did there.
 * Labels (stages, types, statuses) are i18n keys under ADMIN_CRM.
 */

export type CrmKind = 'leads' | 'contacts' | 'clients' | 'partners' | 'investors' | 'press';
/** Kinds shown as a Kanban board (stage + manual sort). */
export type CrmBoardKind = 'leads' | 'partners' | 'investors' | 'press';

export interface CrmLead {
  _id: string;
  company: string; contact: string; email: string; country: string;
  stage: string; value: string; next: string; due: string; tags: string; notes: string; sort: number;
  /** The vendor this lead was turned into (its id), and that vendor as it is now; null when there is none. */
  vendor?: string;
  vendorInfo?: CrmLeadVendor | null;
}
export interface CrmLeadVendor { _id: string; name: string; alias: string; kind: 'prospect' | 'customer' | string }
/** A lead turned into a prospect; `login` only when the prospect was made now (its password is shown once). */
export interface CrmProspectResult {
  lead: CrmLead;
  vendor: CrmLeadVendor;
  created: boolean;
  login?: { email: string; password: string };
}
/** A vendor offered when linking a lead; `lead` is the lead it already belongs to (a vendor has one at most). */
export interface CrmVendorChoice extends CrmLeadVendor {
  lead: { _id: string; company: string } | null;
}
/** How a lead gets its vendor: an existing one, or a new prospect under an alias. */
export type CrmProspectChoice = { vendorId: string } | { alias: string; name?: string };

/**
 * The alias the API derives from a company, as a starting point for a new
 * prospect: a domain gives its name ("bobochic.fr" → "bobochic"), anything
 * else its letters and digits ("Bobochic Paris" → "bobochicparis").
 */
export function aliasForCompany(company: string): string {
  const raw = String(company || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const domain = raw.match(/^(?:https?:\/\/)?(?:www\d?\.)?([a-z0-9-]+)\.[a-z.]{2,}(?:\/.*)?$/);
  return (domain ? domain[1] : raw).replace(/[^a-z0-9]+/g, '').slice(0, 63);
}

export interface CrmContact {
  _id: string;
  company: string; contact: string; title: string; email: string;
  country: string; status: string; source: string; campaign: string; notes: string;
}
export interface CrmClient {
  _id: string;
  company: string; contact: string; email: string; country: string;
  mrr: string; start: string; status: string; notes: string;
}
export interface CrmPartner {
  _id: string;
  name: string; type: string; contact: string; email: string; phone: string;
  country: string; zone: string; network: string; commission: string; deals: string;
  stage: string; next: string; due: string; tags: string; notes: string; sort: number;
}

export interface CrmInvestor {
  _id: string;
  name: string; type: string; contact: string; email: string; country: string;
  ticket: string; focus: string; source: string; msft: string;
  stage: string; amount: string; next: string; due: string; tags: string; notes: string; sort: number;
}
export interface CrmPress {
  _id: string;
  outlet: string; type: string; contact: string; email: string; phone: string; country: string; angle: string;
  stage: string; next: string; due: string; tags: string; notes: string; sort: number;
}

export interface CrmContactsPage {
  total: number; items: CrmContact[]; page: number; pages: number;
  campaigns: string[]; countries: string[]; byStatus: Record<string, number>;
}

/** A board column; `label` is its i18n key. */
export interface CrmStage { id: string; label: string; color: string }

const stages = (group: string, list: [string, string][]): CrmStage[] =>
  list.map(([id, color]) => ({ id, color, label: `ADMIN_CRM.STAGES.${group}.${id}` }));
/** id → i18n key, for the option lists (types, statuses, sources). */
const labels = (group: string, ids: string[]): Record<string, string> =>
  Object.fromEntries(ids.map((id) => [id, `ADMIN_CRM.${group}.${id}`]));

export const LEAD_STAGES: CrmStage[] = stages('LEADS', [
  ['prospect', '#9a9a9e'], ['contacted', '#2456b3'], ['interested', '#a8720a'], ['demo', '#7b4bd0'], ['proposal', '#d06a1f'],
  ['nego', '#c0392b'], ['won', '#1e8e5a'], ['standby', '#7c8894'], ['lost', '#5f5e5c'],
]);

/** Lead stages early enough to turn the lead into a prospect (a vendor its demo is made under). */
export const PROSPECT_STAGES = ['prospect', 'contacted', 'interested', 'demo'];

export const PARTNER_STAGES: CrmStage[] = stages('PARTNERS', [
  ['to_contact', '#9a9a9e'], ['contacted', '#2456b3'], ['discussion', '#a8720a'], ['agreement', '#7b4bd0'], ['active', '#1e8e5a'], ['inactive', '#5f5e5c'],
]);

export const PARTNER_TYPES = labels('PARTNER_TYPES', ['agence', 'consultant', 'integrator', 'distributor', 'association', 'other']);

export const INVESTOR_STAGES: CrmStage[] = stages('INVESTORS', [
  ['sourced', '#9a9a9e'], ['to_contact', '#6b7280'], ['contacted', '#2456b3'], ['meeting', '#7b4bd0'], ['diligence', '#a8720a'],
  ['termsheet', '#d06a1f'], ['committed', '#1e8e5a'], ['passed', '#5f5e5c'],
]);

export const INVESTOR_TYPES = labels('INVESTOR_TYPES', ['angel', 'vc', 'microvc', 'accelerator', 'family_office']);

/** Pre-seed round target, in dollars; progress is tracked on raw amounts (tickets may be EUR or USD). */
export const RAISE_TARGET = 600000;

export const PRESS_STAGES: CrmStage[] = stages('PRESS', [
  ['to_contact', '#9a9a9e'], ['contacted', '#2456b3'], ['discussion', '#a8720a'], ['article_planned', '#7b4bd0'],
  ['published', '#1e8e5a'], ['standby', '#7c8894'], ['declined', '#5f5e5c'],
]);

export const PRESS_TYPES = labels('PRESS_TYPES', ['magazine', 'site', 'podcast', 'newsletter', 'other']);

export const CONTACT_STATUS = labels('CONTACT_STATUS', ['contacte', 'relance', 'repondu', 'interesse', 'refus', 'echec', 'non_contacte']);

export const CLIENT_STATUS = labels('CLIENT_STATUS', ['actif', 'en_attente', 'churn']);

export const CONTACT_SOURCES = labels('CONTACT_SOURCES', ['manual_all', 'manuel initial', 'hubspot', 'outbound', 'inbound']);

/** USD → EUR rate used only to show one consolidated euro figure next to split totals. */
export const USD_EUR = 0.92;

export const FLAGS: Record<string, string> = {
  US: '🇺🇸', FR: '🇫🇷', ES: '🇪🇸', NL: '🇳🇱', DE: '🇩🇪', IT: '🇮🇹', GB: '🇬🇧',
  CA: '🇨🇦', AU: '🇦🇺', NZ: '🇳🇿', IE: '🇮🇪', ZA: '🇿🇦', MX: '🇲🇽', BE: '🇧🇪',
  CH: '🇨🇭', LV: '🇱🇻', TR: '🇹🇷', BR: '🇧🇷', LU: '🇱🇺', SA: '🇸🇦', AE: '🇦🇪',
  VN: '🇻🇳', CN: '🇨🇳', IN: '🇮🇳', MA: '🇲🇦', CL: '🇨🇱', AR: '🇦🇷', PE: '🇵🇪',
  CO: '🇨🇴', EC: '🇪🇨', BO: '🇧🇴', HN: '🇭🇳', HU: '🇭🇺', PT: '🇵🇹',
};

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

/** "2000 USD", "$1,500", "1 500 €" → { n, cur }. Euro when no currency is given. */
export function parseMoney(v?: string): { n: number; cur: 'EUR' | 'USD' } | null {
  if (!v) return null;
  // The currency may follow the amount ("2000 USD") or lead it ("$1,500").
  const m = String(v).match(/(USD|EUR|\$|€)?\s*([\d\s.,]+)\s*(USD|EUR|\$|€)?/i);
  if (!m) return null;
  const n = parseFloat(m[2].replace(/[\s,]/g, ''));
  if (Number.isNaN(n)) return null;
  const raw = (m[3] || m[1] || 'EUR').toUpperCase();
  return { n, cur: raw === 'USD' || raw === '$' ? 'USD' : 'EUR' };
}

export function fmtMonthly(v?: string): string {
  const p = parseMoney(v);
  if (!p) return '';
  return `${p.cur === 'USD' ? '$' : '€'}${p.n.toLocaleString('en-US')} /mo`;
}

/** Sum money strings, kept apart per currency so USD is never folded into euros by accident. */
export function sumMoney(values: (string | undefined)[]): { eur: number; usd: number } {
  return values.reduce(
    (acc, v) => {
      const p = parseMoney(v);
      if (p) p.cur === 'USD' ? (acc.usd += p.n) : (acc.eur += p.n);
      return acc;
    },
    { eur: 0, usd: 0 },
  );
}

export const fmtSplit = ({ eur, usd }: { eur: number; usd: number }): string =>
  [eur || !usd ? `€${eur.toLocaleString('en-US')}` : '', usd ? `$${usd.toLocaleString('en-US')}` : ''].filter(Boolean).join(' + ');

export const eurEquivalent = ({ eur, usd }: { eur: number; usd: number }): string =>
  `≈ €${Math.round(eur + usd * USD_EUR).toLocaleString('en-US')}`;

export const splitTags = (tags?: string): string[] => (tags || '').split(',').map((t) => t.trim()).filter(Boolean);

/** Overdue / due today / later, for colouring a card's next action. */
export function dueState(due?: string): 'overdue' | 'today' | 'later' | null {
  if (!due) return null;
  const t = todayIso();
  return due < t ? 'overdue' : due === t ? 'today' : 'later';
}

/** Manual order first, then the earliest next action. */
export const bySort = <T extends { sort?: number; due?: string }>(a: T, b: T): number => {
  const d = (a.sort ?? 0) - (b.sort ?? 0);
  if (d !== 0) return d;
  return (a.due || '9999') < (b.due || '9999') ? -1 : (a.due || '9999') > (b.due || '9999') ? 1 : 0;
};

/**
 * Drop card `draggedId` onto card `targetId`: splice it into that slot and
 * renumber the column with gaps of 10, so the order survives reloads.
 */
export function reorderWithin<T extends { _id: string }>(column: T[], draggedId: string, targetId: string): { id: string; sort: number }[] {
  const list = [...column];
  const from = list.findIndex((i) => i._id === draggedId);
  const to = list.findIndex((i) => i._id === targetId);
  if (from === -1 || to === -1 || from === to) return [];
  const [moved] = list.splice(from, 1);
  list.splice(to, 0, moved);
  return list.map((i, idx) => ({ id: i._id, sort: idx * 10 }));
}
